import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CLIENT_LIST } from "./atriumData";

interface Item {
  kind: "Page" | "Client";
  id: string;
  label: string;
  to?: string;
  action?: () => void;
}

interface Props {
  open: boolean;
  setOpen: (v: boolean) => void;
}

export default function AtriumCommandPalette({ open, setOpen }: Props) {
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(!open);
      }
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  if (!open) return null;

  const pages: Item[] = [
    { kind: "Page", id: "today",     label: "Today",     to: "/" },
    { kind: "Page", id: "clients",   label: "Clients",   to: "/clients" },
    { kind: "Page", id: "pipeline",  label: "Pipeline",  to: "/pipeline" },
    { kind: "Page", id: "ops",       label: "Ops",       to: "/ops" },
    { kind: "Page", id: "knowledge", label: "Knowledge", to: "/knowledge" },
  ];
  const clients: Item[] = CLIENT_LIST.map((c) => ({
    kind: "Client", id: c.slug, label: c.name, to: `/clients/${c.slug}`,
  }));
  const all: Item[] = [...pages, ...clients];
  const filtered = all.filter(
    (i) => !q || (i.label + i.kind).toLowerCase().includes(q.toLowerCase())
  );

  function activate(i: Item) {
    if (i.to) navigate(i.to);
    else i.action?.();
    setOpen(false);
    setQ("");
  }

  return (
    <div
      onClick={() => setOpen(false)}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 50 }}
      className="flex justify-center pt-[12vh]"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="a-card w-[540px] max-h-[70vh] overflow-hidden flex flex-col"
        style={{ boxShadow: "0 12px 40px rgba(0,0,0,0.4)" }}
      >
        <div
          className="px-[14px] py-3 flex items-center gap-2 border-b"
          style={{ borderColor: "var(--a-border)" }}
        >
          <span style={{ color: "var(--a-muted)" }}>⌕</span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Jump to client or page…"
            className="flex-1 bg-transparent border-0 text-[14px] outline-none"
            style={{ color: "var(--a-text)" }}
          />
          <span className="a-chip">esc</span>
        </div>
        <div className="overflow-y-auto">
          {filtered.slice(0, 12).map((it, i) => (
            <div
              key={`${it.kind}-${it.id}`}
              onClick={() => activate(it)}
              className="flex items-center gap-2.5 px-[14px] py-2.5 text-[13px] cursor-pointer a-row-hover"
              style={{ borderTop: i ? "1px solid var(--a-border)" : "none" }}
            >
              <span
                className="text-[10px] uppercase tracking-[0.06em] w-[60px]"
                style={{ color: "var(--a-muted)" }}
              >
                {it.kind}
              </span>
              <span className="flex-1">{it.label}</span>
              <span className="text-[11px]" style={{ color: "var(--a-faint)" }}>↵</span>
            </div>
          ))}
          {filtered.length === 0 && (
            <div
              className="px-[14px] py-[30px] text-center text-[13px]"
              style={{ color: "var(--a-muted)" }}
            >
              No matches.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
